import React, { useState, useEffect } from 'react';
import { bookingApi } from '../services/api';

const QUICK_CANCEL_REASONS = {
  client: [
    'Schedule conflict',
    'Personal emergency',
    'Booked by mistake / wrong slot',
    'Need to find another therapist',
    'Financial / budget reasons',
    'Other reason',
  ],
  therapist: [
    'Emergency / Clinical conflict',
    'Unavailable during requested slot',
    'Client case requires different specialization',
    'Technical / Connectivity issue',
    'Other reason',
  ],
};

const QUICK_RESCHEDULE_REASONS = {
  client: [
    'Need a different time slot',
    'Work/personal schedule changed',
    'Therapist advised alternate time',
    'Other reason',
  ],
  therapist: [
    'Prior appointment ran over / Slot overlap',
    'Proposed alternative time for better session',
    'Emergency reschedule',
    'Other reason',
  ],
};

export default function BookingActionModal({
  isOpen,
  onClose,
  booking,
  actionType = 'reschedule', // 'reschedule' | 'cancel'
  userRole = 'client', // 'client' | 'therapist' | 'physiotherapist'
  onSuccess,
}) {
  const [scheduledAt, setScheduledAt] = useState('');
  const [reason, setReason] = useState('');
  const [selectedQuickReason, setSelectedQuickReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  const isTherapist = userRole === 'therapist' || userRole === 'physiotherapist';
  const isReschedule = actionType === 'reschedule';

  // Format current booking date for initial display / min bounds
  useEffect(() => {
    if (isOpen && booking) {
      setError('');
      setSuccessMsg('');
      setReason('');
      setSelectedQuickReason('');

      if (isReschedule && booking.scheduledAt) {
        try {
          const d = new Date(booking.scheduledAt);
          // Format to YYYY-MM-DDTHH:MM for datetime-local
          const pad = (n) => String(n).padStart(2, '0');
          const localIso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
          setScheduledAt(localIso);
        } catch (e) {
          setScheduledAt('');
        }
      } else {
        // Default to tomorrow 10:00 AM
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(10, 0, 0, 0);
        const pad = (n) => String(n).padStart(2, '0');
        const localIso = `${tomorrow.getFullYear()}-${pad(tomorrow.getMonth() + 1)}-${pad(tomorrow.getDate())}T10:00`;
        setScheduledAt(localIso);
      }
    }
  }, [isOpen, booking, actionType, isReschedule]);

  if (!isOpen || !booking) return null;

  const nowIso = new Date().toISOString().slice(0, 16);

  // Derive other party name
  const otherPartyName = isTherapist
    ? booking.clientId?.name || booking.clientName || 'Patient'
    : booking.therapistUser?.name ||
      booking.therapistProfile?.name ||
      booking.physiotherapistProfileId?.userId?.name ||
      booking.physiotherapistProfileId?.name ||
      'Mental Health Professional';

  const currentFormattedDate = booking.scheduledAt
    ? new Date(booking.scheduledAt).toLocaleString('en-IN', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Not Set / TBD';

  const handleQuickReasonClick = (r) => {
    setSelectedQuickReason(r);
    if (!reason || reason === selectedQuickReason) {
      setReason(r);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const finalReason = reason.trim() || selectedQuickReason || '';

    try {
      if (isReschedule) {
        if (!scheduledAt) {
          setError('Please select a new session date and time.');
          setLoading(false);
          return;
        }

        const newDate = new Date(scheduledAt);
        if (newDate <= new Date()) {
          setError('Session date must be in the future.');
          setLoading(false);
          return;
        }

        const res = await bookingApi.reschedule(booking._id, {
          scheduledAt: newDate.toISOString(),
          rescheduleReason: finalReason,
        });

        setSuccessMsg(res?.message || 'Booking rescheduled successfully!');
        setTimeout(() => {
          if (onSuccess) onSuccess(res?.booking || booking);
          onClose();
        }, 1200);
      } else {
        // Cancel
        const res = await bookingApi.cancel(booking._id, {
          cancellationReason: finalReason,
        });

        setSuccessMsg(res?.message || 'Booking cancelled successfully!');
        setTimeout(() => {
          if (onSuccess) onSuccess(res?.booking || booking);
          onClose();
        }, 1200);
      }
    } catch (err) {
      console.error(`Failed to ${actionType} booking:`, err);
      setError(err?.message || `Unable to ${actionType} session. Please try again.`);
    } finally {
      setLoading(false);
    }
  };

  const quickReasons = isReschedule
    ? isTherapist
      ? QUICK_RESCHEDULE_REASONS.therapist
      : QUICK_RESCHEDULE_REASONS.client
    : isTherapist
    ? QUICK_CANCEL_REASONS.therapist
    : QUICK_CANCEL_REASONS.client;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(5px)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        animation: 'fadeIn 0.2s ease-out',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget && !loading) onClose();
      }}
    >
      <div
        style={{
          background: '#ffffff',
          borderRadius: '16px',
          width: '100%',
          maxWidth: '540px',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          maxHeight: '90vh',
        }}
      >
        {/* Modal Header */}
        <div
          style={{
            padding: '20px 24px',
            background: isReschedule
              ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'
              : 'linear-gradient(135deg, #dc2626 0%, #991b1b 100%)',
            color: '#ffffff',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '24px' }}>{isReschedule ? '🔄' : '🚫'}</span>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 700, color: '#fff' }}>
                {isReschedule ? 'Reschedule Session' : 'Cancel Session Booking'}
              </h3>
              <p style={{ margin: '2px 0 0 0', fontSize: '12px', opacity: 0.9 }}>
                {isReschedule
                  ? `Choose a new date and time for session with ${otherPartyName}`
                  : `Confirm cancellation for session with ${otherPartyName}`}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: '#fff',
              fontSize: '18px',
              borderRadius: '50%',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            ✕
          </button>
        </div>

        {/* Modal Body */}
        <form onSubmit={handleSubmit} style={{ padding: '24px', overflowY: 'auto' }}>
          {/* Current Session Snapshot */}
          <div
            style={{
              padding: '14px 16px',
              background: '#f8fafc',
              border: '1px solid #e2e8f0',
              borderRadius: '12px',
              marginBottom: '20px',
              fontSize: '13px',
              color: '#334155',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
              <span>
                👤 <strong>{isTherapist ? 'Patient' : 'Therapist'}:</strong> {otherPartyName}
              </span>
              <span
                style={{
                  textTransform: 'capitalize',
                  fontWeight: 700,
                  fontSize: '11px',
                  padding: '2px 8px',
                  borderRadius: '10px',
                  background:
                    booking.status === 'confirmed'
                      ? '#d1fae5'
                      : booking.status === 'pending'
                      ? '#fef3c7'
                      : '#fee2e2',
                  color:
                    booking.status === 'confirmed'
                      ? '#065f46'
                      : booking.status === 'pending'
                      ? '#92400e'
                      : '#991b1b',
                }}
              >
                {booking.status}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>
                📅 <strong>Current Slot:</strong> {currentFormattedDate}
              </span>
              <span>
                {booking.mode === 'online' ? '💻 Online' : '🏥 In-Person'}
              </span>
            </div>
          </div>

          {error && (
            <div
              style={{
                padding: '12px 14px',
                background: '#fee2e2',
                color: '#991b1b',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span>⚠️</span>
              <span>{error}</span>
            </div>
          )}

          {successMsg && (
            <div
              style={{
                padding: '12px 14px',
                background: '#d1fae5',
                color: '#065f46',
                borderRadius: '8px',
                marginBottom: '16px',
                fontSize: '13px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <span>✅</span>
              <span>{successMsg}</span>
            </div>
          )}

          {/* Reschedule Date Time Picker */}
          {isReschedule && (
            <div style={{ marginBottom: '20px' }}>
              <label
                style={{
                  display: 'block',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#1e293b',
                  marginBottom: '6px',
                }}
              >
                Select New Session Date & Time <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <input
                type="datetime-local"
                min={nowIso}
                value={scheduledAt}
                onChange={(e) => setScheduledAt(e.target.value)}
                required
                style={{
                  width: '100%',
                  padding: '10px 14px',
                  border: '1.5px solid #cbd5e1',
                  borderRadius: '8px',
                  fontSize: '14px',
                  color: '#0f172a',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
              <p style={{ margin: '6px 0 0 0', fontSize: '11px', color: '#64748b' }}>
                💡 An updated Google Calendar invite and WhatsApp/email confirmation will be sent automatically.
              </p>
            </div>
          )}

          {/* Quick Reasons selection */}
          <div style={{ marginBottom: '16px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: 600,
                color: '#1e293b',
                marginBottom: '8px',
              }}
            >
              {isReschedule ? 'Reason for Rescheduling' : 'Reason for Cancellation'} (Select quick tag)
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {quickReasons.map((qr) => (
                <button
                  type="button"
                  key={qr}
                  onClick={() => handleQuickReasonClick(qr)}
                  style={{
                    padding: '6px 12px',
                    borderRadius: '16px',
                    fontSize: '12px',
                    fontWeight: 500,
                    border: '1px solid',
                    borderColor: selectedQuickReason === qr ? (isReschedule ? '#7c3aed' : '#dc2626') : '#e2e8f0',
                    background: selectedQuickReason === qr ? (isReschedule ? '#f5f3ff' : '#fef2f2') : '#f8fafc',
                    color: selectedQuickReason === qr ? (isReschedule ? '#6d28d9' : '#b91c1c') : '#475569',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                  }}
                >
                  {qr}
                </button>
              ))}
            </div>
          </div>

          {/* Custom Reason Text Area */}
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'block',
                fontSize: '13px',
                fontWeight: 600,
                color: '#1e293b',
                marginBottom: '6px',
              }}
            >
              Additional Comments / Notes (Optional)
            </label>
            <textarea
              rows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                isReschedule
                  ? 'Provide any extra details about the new time slot or reason...'
                  : 'Explain why you are cancelling this appointment...'
              }
              style={{
                width: '100%',
                padding: '10px 14px',
                border: '1.5px solid #cbd5e1',
                borderRadius: '8px',
                fontSize: '13px',
                color: '#0f172a',
                outline: 'none',
                boxSizing: 'border-box',
                resize: 'vertical',
              }}
            />
          </div>

          {/* Notice Alert */}
          <div
            style={{
              padding: '12px 14px',
              background: isReschedule ? '#eff6ff' : '#fffbeb',
              border: `1px solid ${isReschedule ? '#bfdbfe' : '#fef3c7'}`,
              borderRadius: '8px',
              fontSize: '12px',
              color: isReschedule ? '#1e40af' : '#92400e',
              marginBottom: '24px',
            }}
          >
            {isReschedule ? (
              <span>
                ℹ️ <strong>Note:</strong> Both you and {otherPartyName} will receive an instant notification with the new slot details.
              </span>
            ) : (
              <span>
                ⚠️ <strong>Notice:</strong> Cancelling will mark this session as cancelled and notify {otherPartyName}. If you want to change the time instead, use the <strong>Reschedule</strong> option.
              </span>
            )}
          </div>

          {/* Modal Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              style={{
                padding: '10px 18px',
                borderRadius: '8px',
                border: '1px solid #cbd5e1',
                background: '#ffffff',
                color: '#475569',
                fontSize: '13px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Close
            </button>

            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '10px 22px',
                borderRadius: '8px',
                border: 'none',
                background: isReschedule
                  ? 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)'
                  : 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                color: '#ffffff',
                fontSize: '13px',
                fontWeight: 700,
                cursor: loading ? 'not-allowed' : 'pointer',
                opacity: loading ? 0.7 : 1,
                boxShadow: isReschedule
                  ? '0 4px 12px rgba(79, 70, 229, 0.3)'
                  : '0 4px 12px rgba(220, 38, 38, 0.3)',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
              }}
            >
              {loading ? (
                <>
                  <span
                    style={{
                      display: 'inline-block',
                      width: '14px',
                      height: '14px',
                      border: '2px solid #fff',
                      borderTopColor: 'transparent',
                      borderRadius: '50%',
                      animation: 'spin 0.6s linear infinite',
                    }}
                  />
                  Processing...
                </>
              ) : isReschedule ? (
                'Confirm Reschedule'
              ) : (
                'Confirm Cancellation'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
